/* istanbul ignore file */
/**
 * This file is autogenerated by the CatalinaV2Spec package
 **/

import { ItemSubtypes } from "lib/types/items";

export interface CatalogItem {
  /**
   * Item order workflow
   */
  orderWorkflow?: string;
  /**
   * Button Text for external workflow
   */
  externalButtonText?: string;
  /**
   * Unique taxonomy identifier of the product.
   */
  itemId: string | undefined;

  /**
   * The product's name
   */
  name: string | undefined;

  /**
   * The product's description
   */
  description: string | undefined;

  /**
   * A short description for the product
   */
  shortDescription: string | undefined;

  /**
   * The product manufacturer
   */
  manufacturer?: string;

  /**
   * Logo URL of the product
   */
  logoImageUrl: string | undefined;

  /**
   * image URLs of the product
   */
  imageUrls: string[] | undefined;

  /**
   * Categories associated to the product
   */
  categories: string[] | undefined;

  /**
   * Versions of the product
   */
  versions: Version[] | undefined;

  /**
   * The tags associated to the product
   */
  tags?: string[];

  /**
   * The supported OS for the product
   */
  os?: string[];

  /**
   * The set of links and references for the product
   */
  references?: References;

  /**
   * The product status
   */
  status: string;

  etaDays: number;

  hasStock: boolean;

  /**
   * Controls whether to check if an organization should be blocked from ordering the product once their license limit has been reached.
   */
  requiresLicenseCapValidation: boolean;

  /**
   * Applink product name
   */
  applinkProductName?: string;

  /**
   * Applink instance name
   */
  applinkInstanceName?: string;

  /**
   * Controls whether the product is returnable or not
   */
  isReturnable: boolean;

  /**
   * Indicates the subtype of the catalog item (e.g. local, cloud, hybrid)
   */
  subtype?: ItemSubtypes;

  /**
   * ACME integration configuration for this catalog item.
   *
   * Contains settings for enabling ACME deep links and platform-specific launch URLs.
   */
  acme?: AcmeIntegration;

  /**
   * Auto Generated Formatted Description
   *
   * Contains the description generated by the LLM with format for user display in the PDP.
   * Replaces the PDP item.description when flag is turned on.
   */
  autoGenFormattedDescription?: string;
}

export interface CatalogItemSimplified {
  /**
   * Unique taxonomy identifier of the product.
   */
  itemId: string | undefined;

  /**
   * The product's name
   */
  name: string | undefined;

  /**
   * A short description for the product
   */
  shortDescription: string | undefined;

  /**
   * The product manufacturer
   */
  manufacturer?: string;

  /**
   * Logo URL of the product
   */
  logoImageUrl: string | undefined;

  /**
   * Categories associated to the product
   */
  categories: string[] | undefined;

  /**
   * The tags associated to the product
   */
  tags?: string[];

  /**
   * The supported OS for the product
   */
  os?: string[];

  /**
   * The product status
   */
  status: string;

  etaDays: number;

  hasStock: boolean;

  price?: string;

  currency?: string;

  score?: number;

  itptClassification?: string;
}

export interface References {
  /**
   * Used to redirect the user here instead of the normal ordering flow
   */
  orderRedirectURL?: string;

  /**
   * If present, gives more details about the product status. Usually for Unsactioned or Unavailable products
   */
  statusMoreDetailsURL?: string;
}

export interface Version {
  name?: string;
  /**
   * Version identifier (from taxonomy ID)
   */
  id: string | undefined;

  /**
   * Price of the product
   */
  price: number | undefined;

  /**
   * The recurrence at which the product must be re-purchased (can be parsed with https://momentjs.com/docs/#/durations/)
   */
  recurrence?: string;

  /**
   * Currency code (ISO-4217)
   */
  currency: string | undefined;

  /**
   * The status of the product (sanctioned, unsanctioned or unavailable)
   */
  status: ItemStatus | string | undefined;

  /**
   * The item short description
   */
  shortDescription?: string;

  /**
   * Applink product name
   */
  applinkProductName?: string;

  /**
   * Applink instance name
   */
  applinkInstanceName?: string;

  /**
   * Controls whether this product version is returnable or not
   */
  isReturnable?: boolean;

  /**
   * ACME integration configuration for this product version.
   *
   * Contains settings for enabling ACME deep links and platform-specific launch URLs.
   */
  acme?: AcmeIntegration;
}

export enum ItemStatus {
  sanctioned = "sanctioned",
  unavailable = "unavailable",
  unsanctioned = "unsanctioned",
}

export enum ItemOrderWorkflow {
  NON_ORDERABLE_WORKFLOW = "non-orderable",
}

/**
 * ACME Integration configuration for catalog items.
 *
 * ACME deep links allow users to launch products directly from IT Shop into the ACME application
 *
 */
export interface AcmeIntegration {
  /**
   * Controls whether the product is ACME enabled or not.
   *
   * This flag is `true` indicates that this product can be launched through ACME using deep links.
   * When enabled, users will see a button to open the product directly in ACME.
   *
   * @example
   * Add a specification in the product catalog with label "isAcmeEnabledProduct" and value "true" to enable ACME integration.
   */
  isEnabled: boolean;

  /**
   * Deep link URL for launching this product in ACME on Windows systems.
   *
   * This should be in the format "acme://launchacmeui/softwarecatalog/product_acme_id".
   * Only used when `isEnabled` is `true`.
   *
   * @example
   * "acme://launchacmeui/softwarecatalog/amazon_chime_5"
   *
   * @see {@link macOsDeepLinkUrl} for macOS equivalent
   */
  windowsDeepLinkUrl: string | undefined | null;

  /**
   * Deep link URL for launching this product in ACME on macOS systems.
   *
   * This should be in the format "acme://launchacmeui/softwarecatalog/product_acme_id".
   * Only used when `isEnabled` is `true`.
   *
   * @example
   * "acme://launchacmeui/softwarecatalog/amazon_chime_5"
   *
   * @see {@link windowsDeepLinkUrl} for Windows equivalent
   */
  macOsDeepLinkUrl: string | undefined | null;
}
